import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'crc-audience';

const AudienceContext = createContext(null);

export function AudienceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [socialNudgeDismissed, setSocialNudgeDismissed] = useState(false);

  const audience = useMemo(() => {
    const p = location.pathname;
    if (p === '/schools' || p.startsWith('/schools/')) return 'school';
    return 'student';
  }, [location.pathname]);

  const dismissSocialNudge = useCallback(() => {
    setSocialNudgeDismissed(true);
  }, []);

  const setAudience = useCallback(
    (mode) => {
      const next = mode === 'school' ? '/schools' : '/';
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        /* ignore */
      }
      navigate(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate]
  );

  useEffect(() => {
    const titles = {
      student:
        'ChooseRightCareer | Career DNA and guidance for Class 9 to 12 students',
      school:
        'ChooseRightCareer for schools | Career DNA pilots, bulk assessments, rollout support',
    };
    document.title = titles[audience] || titles.student;
    const meta = document.querySelector('meta[name="description"]');
    const descriptions = {
      student:
        'Career DNA for students who want clarity without the noise. Honest assessment, clear PDF, optional experts. Built with families in mind.',
      school:
        'Career DNA programs for Class 9 to 12 with bulk assessments, family ready PDFs, cohort analytics, and partnership support from pilot to scale.',
    };
    if (meta) meta.setAttribute('content', descriptions[audience] || descriptions.student);
  }, [audience]);

  const value = useMemo(
    () => ({
      audience,
      isSchool: audience === 'school',
      isStudent: audience === 'student',
      setAudience,
      toggle: () => setAudience(audience === 'school' ? 'student' : 'school'),
      socialNudgeDismissed,
      dismissSocialNudge,
    }),
    [audience, dismissSocialNudge, setAudience, socialNudgeDismissed]
  );

  return <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>;
}

export function useAudience() {
  const ctx = useContext(AudienceContext);
  if (!ctx) {
    throw new Error('useAudience must be used within AudienceProvider');
  }
  return ctx;
}
